import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
	ChevronDown,
	ChevronRight,
	KeyRound,
	Loader2,
	Pencil,
	Plus,
	Power,
	RefreshCw,
	Send,
	ShieldOff,
	Trash2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";

import WebhookFormModal from "../../components/WebhookFormModal";
import CredencialRevelada from "../../components/CredencialRevelada";
import PainelDeEntregas from "./PainelDeEntregas";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const quando = (valor) =>
	valor ? new Date(valor).toLocaleString("pt-BR") : "nunca";

const MOTIVOS = {
	failure_streak: "Desligado sozinho: 20 entregas seguidas sem sucesso.",
	revoked: "Credenciais revogadas.",
	manual: "Desligado na mão.",
};

const AbaWebhooks = () => {
	const [webhooks, setWebhooks] = useState([]);
	const [catalogo, setCatalogo] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [aberto, setAberto] = useState(null);
	const [modalAberto, setModalAberto] = useState(false);
	const [emEdicao, setEmEdicao] = useState(null);
	const [ocupado, setOcupado] = useState(null);
	const [revelada, setRevelada] = useState(null);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const [lista, eventos] = await Promise.all([
				api.get("/webhooks"),
				api.get("/webhooks/events"),
			]);
			setWebhooks(lista.data || []);
			setCatalogo(eventos.data.events || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const agir = async (webhook, acao, chamada, mensagem) => {
		setOcupado(`${webhook.id}:${acao}`);
		try {
			const resultado = await chamada();
			if (mensagem) toast.success(mensagem);
			await carregar();
			return resultado;
		} catch (err) {
			toastError(err);
			return null;
		} finally {
			setOcupado(null);
		}
	};

	const testar = (webhook) =>
		agir(
			webhook,
			"test",
			() => api.post(`/webhooks/${webhook.id}/test`),
			"Disparo enviado. Abra as entregas para ver a resposta do destino.",
		);

	const alternar = (webhook) =>
		agir(
			webhook,
			"toggle",
			() =>
				api.post(
					`/webhooks/${webhook.id}/${webhook.isActive ? "disable" : "enable"}`,
				),
			webhook.isActive ? "Desativado." : "Ativado.",
		);

	const rotacionar = async (webhook) => {
		if (
			!window.confirm(
				"Gerar um segredo novo? O atual continua valendo por 24 horas, para você trocar do outro lado sem perder evento.",
			)
		)
			return;

		const resultado = await agir(webhook, "rotate", () =>
			api.post(`/webhooks/${webhook.id}/rotate`, { credencial: "secret" }),
		);

		if (resultado) {
			setRevelada({
				titulo: "Segredo rotacionado",
				valor: resultado.data.valor,
				descricao:
					"O segredo anterior continua aceito por 24 horas. Durante esse tempo a entrega leva as duas assinaturas.",
			});
		}
	};

	const revogar = (webhook) => {
		if (
			!window.confirm(
				"Revogar apaga segredo e token na hora, sem janela, e desativa o webhook. Use quando a credencial vazou. Confirma?",
			)
		)
			return;

		agir(
			webhook,
			"revoke",
			() => api.post(`/webhooks/${webhook.id}/revoke`),
			"Credenciais revogadas e webhook desativado.",
		);
	};

	const excluir = (webhook) => {
		if (
			!window.confirm(
				`Excluir "${webhook.name}"? O histórico de entregas dele vai junto.`,
			)
		)
			return;

		agir(
			webhook,
			"delete",
			() => api.delete(`/webhooks/${webhook.id}`),
			"Webhook excluído.",
		);
	};

	const aoSalvar = (dados, segredo) => {
		carregar();
		if (segredo) {
			setRevelada({
				titulo: "Webhook criado",
				valor: segredo,
				descricao:
					"Use este segredo para conferir a assinatura HMAC-SHA256 que acompanha cada entrega.",
			});
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
					Para o sistema avisar outro sistema seu quando algo acontece aqui —
					um atendimento abriu, uma mensagem chegou. A entrega é assinada,
					tentada de novo quando falha, e tudo fica registrado.
				</p>

				<Button
					onClick={() => {
						setEmEdicao(null);
						setModalAberto(true);
					}}
				>
					<Plus className="h-4 w-4" />
					Novo webhook
				</Button>
			</div>

			<div className="overflow-x-auto rounded-xl border bg-card">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-8" />
							<TableHead>Nome</TableHead>
							<TableHead>Destino</TableHead>
							<TableHead>Eventos</TableHead>
							<TableHead>Estado</TableHead>
							<TableHead>Última entrega</TableHead>
							<TableHead>Segredo</TableHead>
							<TableHead className="text-right">Ações</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{carregando && (
							<TableRow>
								<TableCell colSpan={8} className="py-6 text-center text-sm">
									Carregando…
								</TableCell>
							</TableRow>
						)}

						{!carregando && !webhooks.length && (
							<TableRow>
								<TableCell colSpan={8} className="py-10 text-center">
									<p className="text-sm font-medium">
										Nenhum webhook cadastrado.
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										Comece criando um que avise seu sistema quando um
										atendimento for aberto.
									</p>
								</TableCell>
							</TableRow>
						)}

						{webhooks.map((webhook) => {
							const expandido = aberto === webhook.id;

							return [
								<TableRow key={webhook.id}>
									<TableCell>
										<Button
											variant="ghost"
											size="icon"
											aria-label={
												expandido ? "Fechar entregas" : "Ver entregas"
											}
											onClick={() =>
												setAberto(expandido ? null : webhook.id)
											}
										>
											{expandido ? (
												<ChevronDown className="h-4 w-4" />
											) : (
												<ChevronRight className="h-4 w-4" />
											)}
										</Button>
									</TableCell>

									<TableCell className="font-medium">{webhook.name}</TableCell>

									<TableCell
										className="max-w-[220px] truncate font-mono text-xs"
										title={webhook.url}
									>
										{webhook.url}
									</TableCell>

									<TableCell>
										<div className="flex flex-wrap gap-1">
											{(webhook.events || []).slice(0, 3).map((evento) => (
												<Badge key={evento} variant="secondary">
													{evento}
												</Badge>
											))}
											{(webhook.events || []).length > 3 && (
												<Badge variant="secondary">
													+{webhook.events.length - 3}
												</Badge>
											)}
										</div>
									</TableCell>

									<TableCell>
										{webhook.isActive ? (
											<Badge>Ativo</Badge>
										) : (
											<Badge
												variant="destructive"
												title={MOTIVOS[webhook.disabledReason] || ""}
											>
												Inativo
											</Badge>
										)}
									</TableCell>

									<TableCell className="whitespace-nowrap text-xs">
										{quando(webhook.lastDeliveryAt)}
									</TableCell>

									<TableCell className="font-mono text-xs">
										{webhook.secretHint ? `••••${webhook.secretHint}` : "—"}
									</TableCell>

									<TableCell className="text-right">
										<div className="flex justify-end gap-0.5">
											<Button
												variant="ghost"
												size="icon"
												title="Testar agora"
												disabled={ocupado === `${webhook.id}:test`}
												onClick={() => testar(webhook)}
											>
												{ocupado === `${webhook.id}:test` ? (
													<Loader2 className="h-4 w-4 animate-spin" />
												) : (
													<Send className="h-4 w-4" />
												)}
											</Button>

											<Button
												variant="ghost"
												size="icon"
												title={webhook.isActive ? "Desativar" : "Ativar"}
												onClick={() => alternar(webhook)}
											>
												<Power className="h-4 w-4" />
											</Button>

											<Button
												variant="ghost"
												size="icon"
												title="Rotacionar segredo"
												onClick={() => rotacionar(webhook)}
											>
												<RefreshCw className="h-4 w-4" />
											</Button>

											<Button
												variant="ghost"
												size="icon"
												title="Revogar credenciais"
												onClick={() => revogar(webhook)}
											>
												<ShieldOff className="h-4 w-4" />
											</Button>

											<Button
												variant="ghost"
												size="icon"
												title="Editar"
												onClick={() => {
													setEmEdicao(webhook);
													setModalAberto(true);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>

											<Button
												variant="ghost"
												size="icon"
												title="Excluir"
												onClick={() => excluir(webhook)}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
								</TableRow>,

								expandido && (
									<TableRow key={`${webhook.id}-entregas`}>
										<TableCell colSpan={8} className="p-0">
											<PainelDeEntregas
												webhook={webhook}
												catalogo={catalogo}
											/>
										</TableCell>
									</TableRow>
								),
							];
						})}
					</TableBody>
				</Table>
			</div>

			{webhooks.some((w) => !w.isActive && w.disabledReason) && (
				<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
					<KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					Webhook inativo não recebe evento. Passe o mouse sobre a etiqueta
					para ver o motivo; o botão Testar continua funcionando para você
					conferir antes de religar.
				</p>
			)}

			<WebhookFormModal
				aberto={modalAberto}
				webhook={emEdicao}
				aoFechar={() => setModalAberto(false)}
				aoSalvar={aoSalvar}
			/>

			<CredencialRevelada
				aberto={Boolean(revelada)}
				titulo={revelada?.titulo || ""}
				valor={revelada?.valor || ""}
				descricao={revelada?.descricao || ""}
				aoFechar={() => setRevelada(null)}
			/>
		</div>
	);
};

export default AbaWebhooks;
